import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { queryKeys } from '@/queries/query-keys';

/**
 * Hook to check if the current user has admin permissions
 *
 * Fetches the user profile through the API (Lambda authorizer) and checks the admin flag.
 * Results are cached for 5 minutes to minimize API calls.
 *
 * @returns Object with isAdmin boolean, loading state, and error
 */
export function useAdminStatus() {
  const { user } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.admin.status(),
    queryFn: async () => {
      if (!user?.uid) {
        return { isAdmin: false };
      }

      try {
        const { profile } = await api.getUserProfile();

        if (!profile) {
          console.warn('No user profile found for admin status check');
          return { isAdmin: false };
        }

        return {
          isAdmin: profile.permission_level?.admin === true,
        };
      } catch (error) {
        console.error('Failed to fetch admin status:', error);
        return { isAdmin: false };
      }
    },
    enabled: !!user?.uid,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });

  return {
    isAdmin: data?.isAdmin || false,
    loading: isLoading,
    error,
  };
}
